import { useRef, useState, useEffect, ChangeEvent } from "react";
import Draggable from "react-draggable";
import type { DraggableData, DraggableEvent } from "react-draggable";
import { Button } from "antd";
import {
  PictureOutlined,
  CloseOutlined,
  HolderOutlined,
  FolderOpenOutlined,
  MinusOutlined,
  DownOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import type {
  OverlayKind,
  OverlaySizeKey,
  PersistedOverlayItem,
} from "../../../context/NotesAndPicturesContext";

export type { OverlayKind, OverlaySizeKey, PersistedOverlayItem };
// Backwards-compat alias
export type OverlayItem = PersistedOverlayItem;

// ---------------------------------------------------------------------------
// Sizes
// ---------------------------------------------------------------------------
const SIZES: Record<OverlaySizeKey, { width: number; contentHeight: number }> =
  {
    S: { width: 200, contentHeight: 120 },
    M: { width: 280, contentHeight: 240 },
    L: { width: 400, contentHeight: 400 },
  };

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const OverlayCard = styled.div<{ $width: number }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 400;
  width: ${({ $width }) => $width}px;
  background: #393e46;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: veterantypewriter, monospace;
  color: #eeeeee;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background: #222831;
  cursor: move;
  user-select: none;
  font-size: 12px;
  color: #aaaaaa;
  letter-spacing: 0.5px;
  gap: 6px;
`;

const HandleLabel = styled.span`
  flex: 1;
  text-transform: uppercase;
  font-size: 11px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;

const CloseBtn = styled(Button)`
  height: 22px;
  width: 22px !important;
  min-width: 22px;
  padding: 0;
  border-radius: 4px;
  font-size: 12px;
  background: transparent !important;
  color: #aaaaaa !important;
  border: none !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: #eeeeee !important;
    background: transparent !important;
  }
`;

const ActionBtns = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
`;

const SizeBtn = styled.button<{ $active: boolean }>`
  height: 18px;
  min-width: 18px;
  padding: 0 3px;
  border-radius: 3px;
  font-size: 10px;
  font-family: veterantypewriter, monospace;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? "#8e1616" : "#555")};
  background: ${({ $active }) => ($active ? "#8e1616" : "transparent")};
  color: ${({ $active }) => ($active ? "#eeeeee" : "#aaaaaa")};
  line-height: 1;
  &:hover {
    border-color: #8e1616;
    color: #eeeeee;
  }
`;

const NoteTextArea = styled.textarea<{ $height: number }>`
  width: 100%;
  min-height: ${({ $height }) => $height}px;
  resize: vertical;
  background: transparent;
  border: none;
  outline: none;
  color: #eeeeee;
  font-family: veterantypewriter, monospace;
  font-size: 16px;
  padding: 10px 12px;
  box-sizing: border-box;
  line-height: 1.5;
  ::placeholder {
    color: #666;
  }
`;

const PictureBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
`;

const PictureInputRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const UrlInput = styled.input`
  flex: 1;
  background: #222831;
  border: 1px solid #555;
  border-radius: 4px;
  color: #eeeeee;
  font-family: veterantypewriter, monospace;
  font-size: 12px;
  padding: 4px 8px;
  outline: none;
  min-width: 0;
  &::placeholder {
    color: #666;
  }
  &:focus {
    border-color: #8e1616;
  }
`;

const SmallBtn = styled(Button)`
  height: 26px;
  min-width: 26px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 13px;
  background: #222831 !important;
  color: #aaaaaa !important;
  border: 1px solid #555 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover {
    color: #eeeeee !important;
    border-color: #8e1616 !important;
  }
`;

const PreviewImage = styled.img<{ $height: number }>`
  width: 100%;
  border-radius: 4px;
  display: block;
  object-fit: contain;
  max-height: ${({ $height }) => $height}px;
  filter: grayscale(100%);
`;

const EmptyPicture = styled.div`
  min-height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #555;
  font-size: 12px;
  svg {
    font-size: 28px;
    opacity: 0.35;
  }
`;

// ---------------------------------------------------------------------------
// Picture viewer sub-component
// ---------------------------------------------------------------------------
function PictureViewer({
  initialUrl,
  onLabelChange,
  onUrlChange,
  contentHeight,
}: {
  initialUrl: string | null;
  onLabelChange: (label: string | null) => void;
  onUrlChange: (url: string | null) => void;
  contentHeight: number;
}) {
  const [src, setSrc] = useState<string | null>(initialUrl ?? null);
  const [urlDraft, setUrlDraft] = useState(() => {
    if (!initialUrl || initialUrl.startsWith("data:")) return "";
    return initialUrl;
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setSrc(dataUrl);
      onUrlChange(dataUrl);
    };
    reader.readAsDataURL(file);
    setUrlDraft("");
    onLabelChange(file.name);
  };

  const applyUrl = (value: string) => {
    const trimmed = value.trim();
    setSrc(trimmed || null);
    onUrlChange(trimmed || null);
    if (trimmed) {
      try {
        const name = new URL(trimmed).pathname.split("/").pop() || trimmed;
        onLabelChange(name);
      } catch {
        onLabelChange(trimmed);
      }
    } else {
      onLabelChange(null);
    }
  };

  return (
    <PictureBody>
      <PictureInputRow>
        <UrlInput
          placeholder="Paste image URL…"
          value={urlDraft}
          onChange={(e) => {
            setUrlDraft(e.target.value);
            applyUrl(e.target.value);
          }}
          onMouseDown={(e) => e.stopPropagation()}
        />
        <SmallBtn
          icon={<FolderOpenOutlined />}
          title="Browse file"
          onClick={() => fileInputRef.current?.click()}
          onMouseDown={(e) => e.stopPropagation()}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFile}
        />
      </PictureInputRow>

      {src ? (
        <PreviewImage src={src} alt="preview" $height={contentHeight} />
      ) : (
        <EmptyPicture>
          <PictureOutlined />
          <span>Browse or paste a URL</span>
        </EmptyPicture>
      )}
    </PictureBody>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function DraggableOverlay({
  item,
  onClose,
  onChange,
}: {
  item: PersistedOverlayItem;
  onClose: () => void;
  onChange: (patch: Partial<Omit<PersistedOverlayItem, "id" | "kind">>) => void;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [pictureLabel, setPictureLabel] = useState<string | null>(() => {
    const url = item.pictureUrl;
    if (!url) return null;
    if (url.startsWith("data:")) return "Picture";
    try {
      return new URL(url).pathname.split("/").pop() || url;
    } catch {
      return url;
    }
  });
  const [noteText, setNoteText] = useState(item.noteText);
  const [size, setSize] = useState<OverlaySizeKey>(item.size);
  const [collapsed, setCollapsed] = useState(item.collapsed);
  const [pos, setPos] = useState(item.position);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  });

  const computeBounds = () => {
    if (nodeRef.current) {
      setBounds({
        left: 0,
        top: 0,
        right: window.innerWidth - nodeRef.current.offsetWidth,
        bottom: window.innerHeight - nodeRef.current.offsetHeight,
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const node = nodeRef.current;
      if (!node) return;
      const maxX = window.innerWidth - node.offsetWidth;
      const maxY = window.innerHeight - node.offsetHeight;
      setBounds({ left: 0, top: 0, right: maxX, bottom: maxY });
      setPos((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSizeChange = (s: OverlaySizeKey) => {
    setSize(s);
    onChange({ size: s });
  };

  const handleNoteChange = (text: string) => {
    setNoteText(text);
    onChange({ noteText: text });
  };

  const handleDrag = (_: DraggableEvent, data: DraggableData) => {
    setPos({ x: data.x, y: data.y });
  };

  const handleDragStop = (_: DraggableEvent, data: DraggableData) => {
    setPos({ x: data.x, y: data.y });
    onChange({ position: { x: data.x, y: data.y } });
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      cancel="input, textarea, button, a, .ant-btn, .ant-btn-icon, .anticon"
      position={pos}
      bounds={bounds}
      onStart={computeBounds}
      onDrag={handleDrag}
      onStop={handleDragStop}
    >
      <OverlayCard ref={nodeRef} $width={SIZES[size].width}>
        <DragHandle className="drag-handle">
          <HolderOutlined style={{ fontSize: 14, opacity: 0.5 }} />
          <HandleLabel>
            {item.kind === "note"
              ? noteText.trim() || "Note"
              : (pictureLabel ?? "Picture")}
          </HandleLabel>
          {(["S", "M", "L"] as OverlaySizeKey[]).map((s) => (
            <SizeBtn
              key={s}
              $active={size === s}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => handleSizeChange(s)}
            >
              {s}
            </SizeBtn>
          ))}
          <ActionBtns>
            <CloseBtn
              icon={collapsed ? <DownOutlined /> : <MinusOutlined />}
              aria-label={collapsed ? "Expand" : "Collapse"}
              onClick={() => {
                setCollapsed((c) => {
                  onChange({ collapsed: !c });
                  return !c;
                });
              }}
              onMouseDown={(e) => e.stopPropagation()}
            />
            <CloseBtn
              icon={<CloseOutlined />}
              aria-label="Close"
              onClick={onClose}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </ActionBtns>
        </DragHandle>

        <div style={{ display: collapsed ? "none" : "contents" }}>
          {item.kind === "note" ? (
            <NoteTextArea
              placeholder="Write your note…"
              autoFocus
              $height={SIZES[size].contentHeight}
              value={noteText}
              onChange={(e) => handleNoteChange(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            />
          ) : (
            <PictureViewer
              initialUrl={item.pictureUrl}
              onLabelChange={setPictureLabel}
              onUrlChange={(url) => onChange({ pictureUrl: url })}
              contentHeight={SIZES[size].contentHeight}
            />
          )}
        </div>
      </OverlayCard>
    </Draggable>
  );
}
